import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Stack,
  Link,
  Divider,
  Fade,
  Slide,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import SEOHead from "../components/SEOHead";
import CitabilitySection from "../components/CitabilitySection";
import {
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  Email as EmailIcon,
  Palette as PaletteIcon,
  Favorite as FavoriteIcon,
  Group as GroupIcon,
  EmojiEvents as EmojiEventsIcon,
  Spa as SpaIcon,
  LocalFlorist as LocalFloristIcon,
  Nature as NatureIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

const About: React.FC = () => {
  const values = [
    {
      icon: <PaletteIcon sx={{ fontSize: 36, color: "#8BC34A" }} />,
      title: "創意無限",
      description: "激發每個人的創意潛能，讓藝術融入生活",
    },
    {
      icon: <FavoriteIcon sx={{ fontSize: 36, color: "#689F38" }} />,
      title: "用心製作",
      description: "以愛心和耐心指導每位學員，確保最佳學習體驗",
    },
    {
      icon: <GroupIcon sx={{ fontSize: 36, color: "#8BC34A" }} />,
      title: "社群連結",
      description: "建立溫暖的手工藝社群，分享快樂與創意",
    },
    {
      icon: <EmojiEventsIcon sx={{ fontSize: 36, color: "#689F38" }} />,
      title: "品質保證",
      description: "使用優質材料，確保作品的美觀與安全",
    },
  ];

  const targetAudience = [
    "發展個人興趣 - 尋找放鬆身心的創意活動",
    "情侶朋友 - 一起體驗手工藝的樂趣",
    "家庭親子 - 增進親子關係的互動時光",
    "企業團建 - 團隊建設與創意激發活動",
    "中學生涯規劃 - 體驗手工技藝, 探索自我與未來方向",
  ];

  const faqs = [
    {
      q: "CosyLab 提供哪些類型的手工藝體驗？",
      a: "CosyLab 主理不同主題的療癒手作體驗，常見包括蠟燭製作、植物護理與多元化 DIY 課程。內容會按參加者年齡與興趣調整。",
    },
    {
      q: "是否適合沒有手作經驗的人參加？",
      a: "適合。導師會提供清晰示範與步驟指導，確保每位學員都能完成作品，同時在過程中享受療癒氛圍。",
    },
    {
      q: "如何查詢到校或工作坊相關資訊？",
      a: "你可以使用頁面上的 WhatsApp 或「聯絡我們」入口聯繫 CosyLab。我們會了解需求後提供合適的到校課程/工作坊方案。",
    },
  ];

  const structuredData = useMemo(() => {
    const baseUrl = "https://cosylabdesigns.com";
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首頁",
            item: `${baseUrl}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "關於我們",
            item: `${baseUrl}/about`,
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `
        radial-gradient(ellipse at top left, rgba(139, 195, 74, 0.08) 0%, transparent 50%),
        radial-gradient(ellipse at bottom right, rgba(104, 159, 56, 0.06) 0%, transparent 50%),
        linear-gradient(180deg, rgba(250, 251, 248, 1) 0%, rgba(245, 248, 242, 1) 100%)
      `,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
          repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(139, 195, 74, 0.02) 35px, rgba(139, 195, 74, 0.02) 70px),
          repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(104, 159, 56, 0.02) 35px, rgba(104, 159, 56, 0.02) 70px)
        `,
          pointerEvents: "none",
          zIndex: 0,
        },
      }}
    >
      <SEOHead
        title="關於我們 - CosyLab "
        description="CosyLab 秉持「創造療癒感」的初心，從2021年開始，陪伴過千位參加者走進手作的慢生活。
                我們開設專業手工蠟燭課程，也提供療癒系手作體驗，已與20多間企業、學校與機構攜手合作，讓手作成為大家的小確幸。"
        canonical="https://cosylabdesigns.com/about"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 10 },
          zIndex: 1,
        }}
      >
        <Container maxWidth="xl">
          <Slide direction="down" in={true} timeout={800}>
            <Box textAlign="center" sx={{ mb: 8 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  mb: 4,
                  px: 4,
                  py: 2,
                  backgroundColor: "rgba(139, 195, 74, 0.1)",
                  borderRadius: "50px",
                  border: "1px solid rgba(139, 195, 74, 0.2)",
                }}
              >
                <SpaIcon
                  sx={{
                    fontSize: 24,
                    color: "#689F38",
                    mr: 2,
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: "#558B2F",
                    fontWeight: 500,
                    letterSpacing: 0.5,
                  }}
                >
                  療癒手作體驗
                </Typography>
              </Box>

              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 300,
                  mb: 4,
                  fontSize: { xs: "3rem", sm: "3.5rem", md: "4.5rem" },
                  lineHeight: 1.1,
                  fontFamily: "'Georgia', serif",
                  background:
                    "linear-gradient(135deg, #33691E 0%, #689F38 50%, #9CCC65 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: -0.5,
                }}
              >
                關於
                <Box component="span" sx={{ fontWeight: 600 }}>
                  我們
                </Box>
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  maxWidth: 700,
                  mx: "auto",
                  lineHeight: 1.7,
                  fontSize: { xs: "1.1rem", md: "1.3rem" },
                  color: "text.secondary",
                  fontFamily: "'Source Serif Pro', serif",
                  fontWeight: 400,
                  mb: 6,
                }}
              >
                由專業手工藝導師主理，使用多元化素材進行DIY課程，為客人提供個性化創作體驗
              </Typography>

              {/* Organic Elements */}
              <Box
                sx={{
                  display: "flex",
                  gap: 6,
                  justifyContent: "center",
                  opacity: 0.8,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LocalFloristIcon
                    sx={{
                      fontSize: 28,
                      color: "#8BC34A",
                      mr: 2,
                      transform: "rotate(15deg)",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: "#689F38", fontWeight: 500 }}
                  >
                    2021年成立
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <NatureIcon
                    sx={{
                      fontSize: 28,
                      color: "#689F38",
                      mr: 2,
                      transform: "rotate(-10deg)",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: "#689F38", fontWeight: 500 }}
                  >
                    過千位學員
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Slide>
        </Container>
      </Box>

      {/* Main Content */}
      <Container
        maxWidth="xl"
        sx={{ mb: { xs: 8, md: 12 }, zIndex: 1, position: "relative" }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.8fr 1fr" },
            gap: { xs: 6, md: 8 },
          }}
        >
          {/* Left Column - Service Target */}
          <Fade in={true} timeout={1000}>
            <Box>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, md: 5 },
                  borderRadius: "32px",
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(139, 195, 74, 0.15)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 20px 60px rgba(104, 159, 56, 0.1)",
                    border: "1px solid rgba(139, 195, 74, 0.2)",
                  },
                }}
              >
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    fontWeight: 300,
                    mb: 4,
                    fontSize: { xs: "2rem", md: "2.5rem" },
                    fontFamily: "'Georgia', serif",
                    background:
                      "linear-gradient(135deg, #33691E 0%, #689F38 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: -0.5,
                  }}
                >
                  服務
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    對象
                  </Box>
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: "1.1rem", md: "1.2rem" },
                    lineHeight: 1.7,
                    color: "text.secondary",
                    mb: 4,
                    fontFamily: "'Source Serif Pro', serif",
                  }}
                >
                  歡迎所有對手工藝感興趣的朋友，課程適合：
                </Typography>

                <Stack spacing={3}>
                  {targetAudience.map((item, index) => (
                    <Fade in={true} timeout={1200 + index * 150} key={index}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 3,
                          p: 3,
                          borderRadius: "20px",
                          backgroundColor: "rgba(139, 195, 74, 0.05)",
                          border: "1px solid rgba(139, 195, 74, 0.1)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: "rgba(139, 195, 74, 0.08)",
                            transform: "translateX(4px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: "#689F38",
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: { xs: "1rem", md: "1.1rem" },
                            color: "text.primary",
                            fontWeight: 500,
                            lineHeight: 1.6,
                          }}
                        >
                          {item}
                        </Typography>
                      </Box>
                    </Fade>
                  ))}
                </Stack>
              </Paper>
            </Box>
          </Fade>

          {/* Right Column */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {/* Values Section */}
            <Fade in={true} timeout={1400}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, md: 5 },
                  borderRadius: "32px",
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(139, 195, 74, 0.15)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-4px) rotate(1deg)",
                    boxShadow: "0 20px 60px rgba(104, 159, 56, 0.1)",
                    border: "1px solid rgba(139, 195, 74, 0.2)",
                  },
                }}
              >
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    fontWeight: 300,
                    mb: 4,
                    fontSize: { xs: "2rem", md: "2.5rem" },
                    fontFamily: "'Georgia', serif",
                    background:
                      "linear-gradient(135deg, #33691E 0%, #689F38 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: -0.5,
                  }}
                >
                  核心
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    價值
                  </Box>
                </Typography>

                <Stack spacing={4}>
                  {values.map((value, index) => (
                    <Fade in={true} timeout={1600 + index * 200} key={index}>
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                            mb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: "50%",
                              backgroundColor: "rgba(139, 195, 74, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.3s ease",
                            }}
                          >
                            {value.icon}
                          </Box>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 600,
                              color: "#2E7D32",
                              fontFamily: "'Georgia', serif",
                              fontSize: { xs: "1.2rem", md: "1.3rem" },
                            }}
                          >
                            {value.title}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            color: "text.secondary",
                            lineHeight: 1.7,
                            ml: 12,
                            fontSize: { xs: "0.95rem", md: "1rem" },
                            fontFamily: "'Source Serif Pro', serif",
                          }}
                        >
                          {value.description}
                        </Typography>
                        {index < values.length - 1 && (
                          <Divider
                            sx={{
                              mt: 4,
                              borderColor: "rgba(139, 195, 74, 0.2)",
                              opacity: 0.5,
                            }}
                          />
                        )}
                      </Box>
                    </Fade>
                  ))}
                </Stack>
              </Paper>
            </Fade>

            {/* FAQ Section */}
            <Box
              component="section"
              sx={{
                py: { xs: 6, md: 8 },
                backgroundColor: "rgba(139,195,74,0.05)",
              }}
            >
              <Container maxWidth="md">
                <Fade in={true} timeout={1000}>
                  <Box>
                    <Typography
                      variant="h2"
                      component="h2"
                      textAlign="center"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "1.5rem", md: "2rem" },
                        color: "#2D5A27",
                        mb: 5,
                      }}
                    >
                      常見問題
                    </Typography>
                    {faqs.map((faq, i) => (
                      <Accordion
                        key={i}
                        disableGutters
                        elevation={0}
                        sx={{
                          mb: 1.5,
                          border: "1px solid rgba(139, 195, 74, 0.2)",
                          borderRadius: "12px !important",
                          "&:before": { display: "none" },
                          "&.Mui-expanded": {
                            boxShadow: "0 4px 16px rgba(139, 195, 74, 0.12)",
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={
                            <ExpandMoreIcon sx={{ color: "#2D5A27" }} />
                          }
                          sx={{ px: 3, py: 1.5 }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: "#2D5A27",
                              fontSize: "0.95rem",
                            }}
                          >
                            {faq.q}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                          <Typography sx={{ color: "#555", lineHeight: 1.8 }}>
                            {faq.a}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                </Fade>
              </Container>
            </Box>

            {/* Contact Info */}
            <Fade in={true} timeout={2000}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, md: 5 },
                  borderRadius: "32px",
                  background: `
                    linear-gradient(135deg, rgba(139, 195, 74, 0.08) 0%, rgba(104, 159, 56, 0.05) 100%),
                    radial-gradient(ellipse at center, rgba(255, 255, 255, 0.9) 0%, rgba(250, 251, 248, 0.95) 100%)
                  `,
                  backdropFilter: "blur(20px)",
                  border: "2px solid rgba(139, 195, 74, 0.15)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: "-50%",
                    left: "-50%",
                    width: "200%",
                    height: "200%",
                    background: `
                      repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(139, 195, 74, 0.03) 20px, rgba(139, 195, 74, 0.03) 40px),
                      repeating-linear-gradient(-45deg, transparent, transparent 20px, rgba(104, 159, 56, 0.03) 20px, rgba(104, 159, 56, 0.03) 40px)
                    `,
                  },
                }}
              >
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  <Typography
                    variant="h3"
                    component="h2"
                    sx={{
                      fontWeight: 300,
                      mb: 4,
                      fontSize: { xs: "2rem", md: "2.5rem" },
                      fontFamily: "'Georgia', serif",
                      background:
                        "linear-gradient(135deg, #33691E 0%, #689F38 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      letterSpacing: -0.5,
                    }}
                  >
                    聯絡
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      我們
                    </Box>
                  </Typography>

                  <Stack spacing={3}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        p: 3,
                        borderRadius: "20px",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(139, 195, 74, 0.1)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <WhatsAppIcon sx={{ color: "#25D366", fontSize: 28 }} />
                      <Link
                        href="https://wa.me/85284378839"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="WhatsApp: 84378839"
                        sx={{
                          color: "text.primary",
                          fontWeight: 500,
                          fontSize: { xs: "1rem", md: "1.1rem" },
                          textDecoration: "none",
                          "&:hover": {
                            color: "#25D366",
                          },
                        }}
                      >
                        WhatsApp: 84378839
                      </Link>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        p: 3,
                        borderRadius: "20px",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(139, 195, 74, 0.1)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <InstagramIcon sx={{ color: "#E4405F", fontSize: 28 }} />
                      <Link
                        href="https://www.instagram.com/cosylab.craft/"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Instagram: @cosylab.craft"
                        sx={{
                          color: "text.primary",
                          fontWeight: 500,
                          fontSize: { xs: "1rem", md: "1.1rem" },
                          textDecoration: "none",
                          "&:hover": {
                            color: "#E4405F",
                          },
                        }}
                      >
                        @cosylab.craft
                      </Link>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        p: 3,
                        borderRadius: "20px",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(139, 195, 74, 0.1)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <EmailIcon sx={{ color: "#689F38", fontSize: 28 }} />
                      <Link
                        href="mailto:cosylabhk@gmail.com"
                        title="Email: cosylabhk@gmail.com"
                        sx={{
                          color: "text.primary",
                          fontWeight: 500,
                          fontSize: { xs: "1rem", md: "1.1rem" },
                          textDecoration: "none",
                          "&:hover": {
                            color: "#689F38",
                          },
                        }}
                      >
                        cosylabhk@gmail.com
                      </Link>
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            </Fade>
          </Box>
        </Box>
      </Container>

      <CitabilitySection routeKey="/about" />
    </Box>
  );
};

export default About;
